import { AppBar, Box, SxProps, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { ellipsisStyle } from '../utils/style.utils';
import BackButton from './BackButton';

export interface TopAppBarProps {
  startAction?: React.ReactNode;
  trailingAction?: React.ReactNode;
  backTo: string;
  configuration: 'small' | 'medium';
  title: string;
  shouldCheckHistory?: boolean;
  sx?: SxProps;
}

export default function TopAppBar({ startAction, trailingAction, backTo, title, configuration, shouldCheckHistory = true, sx }: TopAppBarProps): React.JSX.Element {
  const [onScroll, setOnScroll] = useState(false);
  const isSmallConfig = configuration === 'small';

  useEffect(() => {
    const listener = (): void => {
      setOnScroll(!!window.scrollY);
    };

    window.addEventListener('scroll', listener);

    return () => window.removeEventListener('scroll', listener);
  }, []);

  const backButton = useMemo(() => {
    return (
      <BackButton
        to={backTo}
        size="large"
        compact
        shouldCheckHistory={shouldCheckHistory}
      />
    );
  }, [backTo]);

  const titleComponent = useMemo(() => {
    return (
      <Typography
        variant={isSmallConfig ? 'titleLarge' : 'headlineSmall'}
        sx={{
          paddingX: isSmallConfig ? 0 : 2,
          width: isSmallConfig ? '100%' : 'calc(100% - 32px)',
          ...ellipsisStyle,
        }}
      >
        {title}
      </Typography>
    );
  }, [title]);

  return (
    <AppBar
      elevation={0}
      configuration={configuration}
      sx={{
        backgroundColor: onScroll ? 'surfaceContainer.main' : 'surface.main',
        display: {
          xs: 'flex',
          lg: 'none',
        },
        ...sx,
      }}
    >
      {configuration === 'small' && (
        <>
          {startAction || backButton}
          {titleComponent}
          {trailingAction}
        </>
      )}

      {configuration === 'medium' && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {startAction || backButton}
            {trailingAction}
          </Box>
          {titleComponent}
        </>
      )}
    </AppBar>
  );
}
